/**
 * Helpers d'appels API authentifiés via Firebase JWT (Authorization: Bearer <token>).
 * Remplace l'ancien header X-User-Id qui était non sécurisé.
 */
import { auth } from '../firebase';
import { onIdTokenChanged, User } from 'firebase/auth';

export const API = 'https://aglomera.com/api';

// ── Auth state au niveau module ─────────────────────────────────────────────
//
// Pourquoi onIdTokenChanged et pas authStateReady() / auth.currentUser ?
//
// authStateReady() résout dès que l'état LOCAL est lu depuis IndexedDB, mais
// Firebase fait ensuite un reloadAndSetCurrentUserOrClear() (appel réseau) pour
// valider le token. Pendant ce reload, currentUser peut être nul ou invalide.
//
// onIdTokenChanged fire uniquement après qu'un token FRAIS et VALIDÉ existe
// (ou null si l'utilisateur n'est pas connecté). C'est la garantie qu'on veut.
//
let _user: User | null = null;
let _ready = false;
const _readyCbs: Array<() => void> = [];

onIdTokenChanged(auth, (user) => {
  _user = user;
  if (!_ready) {
    _ready = true;
    _readyCbs.splice(0).forEach(cb => cb());
  }
});

/** Attend le premier fire de onIdTokenChanged (auth Firebase initialisée). */
function waitReady(): Promise<void> {
  if (_ready) return Promise.resolve();
  return new Promise<void>(resolve => { _readyCbs.push(resolve); });
}

/**
 * Retourne les headers HTTP pour une requête authentifiée.
 * Attend que Firebase ait un token valide avant de l'inclure.
 */
export async function apiHeaders(
  extra: Record<string, string> = {}
): Promise<Record<string, string>> {
  await waitReady();
  // getIdToken() gère le refresh automatique si le token est expiré
  const token = _user ? await _user.getIdToken() : '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

/**
 * Wrapper fetch authentifié — équivalent à fetch() avec token Firebase auto-injecté.
 */
export async function apiFetch(
  path: string,
  options: RequestInit & { headers?: Record<string, string> } = {}
): Promise<Response> {
  const base = await apiHeaders(options.headers || {});
  return fetch(`${API}${path}`, {
    ...options,
    headers: base,
  });
}
